// src/components/Dashboard/PlayerDashboard/MyApplicationsWidget.jsx
// Shows pending club and program applications

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaPaperPlane, FaBuilding, FaClipboardList, FaClock,
  FaCheckCircle, FaTimesCircle, FaHourglass, FaArrowRight
} from 'react-icons/fa';
import styles from './MyApplicationsWidget.module.css';

const MyApplicationsWidget = ({ pendingClubApps, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [programApps, setProgramApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch program applications
  useEffect(() => {
    const fetchProgramApps = async () => {
      if (!userProfile?._id) {
        setProgramApps([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!token) {
          setLoading(false);
          return;
        }

        // Try to fetch user's program applications
        const response = await fetch(`${backendUrl}/api/registrations/user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Filter for pending/processing applications
          const pending = (data || []).filter(app => 
            app.status === 'pending' || 
            app.status === 'processing' || 
            app.status === 'waitlisted' ||
            app.status === 'approved' // Approved but may need action
          );
          setProgramApps(pending);
        }
      } catch (error) {
        console.error('Error fetching program applications:', error);
        setProgramApps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramApps();
  }, [userProfile?._id, refreshTrigger]);

  const totalApps = (pendingClubApps?.length || 0) + programApps.length;

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <FaCheckCircle className={styles.statusApproved} />;
      case 'rejected':
      case 'denied':
        return <FaTimesCircle className={styles.statusRejected} />;
      case 'waitlisted':
        return <FaHourglass className={styles.statusWaitlist} />;
      default:
        return <FaClock className={styles.statusPending} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'Approved';
      case 'rejected':
      case 'denied':
        return 'Not Approved';
      case 'waitlisted':
        return 'Waitlisted';
      case 'processing':
        return 'Processing';
      default:
        return 'Pending';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleClubClick = (clubId) => {
    navigate(`/club/${clubId}`);
  };

  const handleProgramClick = (programId) => {
    navigate(`/program/${programId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaPaperPlane className={styles.headerIcon} />
            <h3 className={styles.headerTitle}>My Applications</h3>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaPaperPlane className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Applications</h3>
        </div>
        {totalApps > 0 && (
          <span className={styles.badge}>{totalApps}</span>
        )}
      </div>

      <div className={styles.content}>
        {totalApps === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>📨</span>
            <p>No pending applications</p>
            <span className={styles.emptyHint}>Your applications will appear here</span>
          </div>
        ) : (
          <div className={styles.applicationsList}>
            {/* Club Membership Applications */}
            {pendingClubApps?.map((membership, index) => {
              const club = membership.club;
              const clubId = club?._id || club;
              const clubName = club?.name || 'Club';
              
              return (
                <div 
                  key={`club-${clubId}-${index}`}
                  className={styles.appCard}
                  onClick={() => handleClubClick(clubId)}
                >
                  <div className={styles.appIcon}>
                    <FaBuilding />
                  </div>
                  <div className={styles.appInfo}>
                    <span className={styles.appType}>Club Membership</span>
                    <span className={styles.appName}>{clubName}</span>
                    <div className={styles.appMeta}>
                      {getStatusIcon(membership.status)}
                      <span className={styles.appStatus}>
                        {getStatusLabel(membership.status)}
                      </span>
                      {membership.createdAt && (
                        <span className={styles.appDate}>
                          • Applied {formatDate(membership.createdAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <FaArrowRight className={styles.appArrow} />
                </div>
              );
            })}

            {/* Program Applications */}
            {programApps.map((app, index) => (
              <div 
                key={`program-${app._id || index}`}
                className={styles.appCard}
                onClick={() => handleProgramClick(app.programId?._id || app.programId)}
              >
                <div className={styles.appIcon}>
                  <FaClipboardList />
                </div>
                <div className={styles.appInfo}>
                  <span className={styles.appType}>Program Registration</span>
                  <span className={styles.appName}>
                    {app.programId?.name || app.programName || 'Program'}
                  </span>
                  <div className={styles.appMeta}>
                    {getStatusIcon(app.status)}
                    <span className={styles.appStatus}>
                      {getStatusLabel(app.status)}
                    </span>
                    {app.createdAt && (
                      <span className={styles.appDate}>
                        • Applied {formatDate(app.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
                <FaArrowRight className={styles.appArrow} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplicationsWidget;



